import { memo } from 'react';

import { RelatedInquiryCard } from './RelatedInquiryCard';

import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import type { AskResult } from '@/services/ask/api';

type AskResultCardProps = {
  result: AskResult;
  position: number;
  isRepeat: boolean;
  isSelected: boolean;
  onSelect: (result: AskResult, position: number) => void;
};

/**
 * Holds a result's rank position when there is nothing to show in it: the
 * inquiry has become ineligible since the turn ran, or the result is from a
 * source this build does not know yet. Keeping the slot means position 3 is
 * still the third result the search returned.
 */
export function UnavailableResultCard({ position }: { position: number }) {
  return (
    <VStack
      className="w-full rounded-xl border border-dashed border-outline-500 bg-background-0 p-4"
      accessibilityLabel={`Result ${position + 1}: no longer available`}>
      <Text className="text-sm text-typography-700">This result is no longer available.</Text>
    </VStack>
  );
}

/**
 * One ranked result, drawn by its `source`. Only inquiries ship today; other
 * sources are planned, so an unknown one renders the placeholder rather than
 * failing the turn.
 */
export const AskResultCard = memo(function AskResultCard({
  result,
  position,
  isRepeat,
  isSelected,
  onSelect,
}: AskResultCardProps) {
  if (result.unavailable) {
    return <UnavailableResultCard position={position} />;
  }

  switch (result.source) {
    case 'inquiry':
      return (
        <RelatedInquiryCard
          result={result}
          position={position}
          isRepeat={isRepeat}
          isSelected={isSelected}
          onSelect={() => onSelect(result, position)}
        />
      );
    default:
      return <UnavailableResultCard position={position} />;
  }
});
